import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWishlistProducts } from '../../Services/Wishlist';
import ProductCard from '../../components/common/ProductCardComponent/Index';
import Spinner from '../../components/common/loadingSpinnercomponent/Spinner';
import ProductViewModal from '../../components/common/modalComponent/productModal';
import TransitionLoader from '../../components/common/TransitionLoader';
import { clearWishlist } from '../../slices/wishlistSlice';
import { toast } from 'react-toastify';
import './Wishlist.css';

const WishlistPage = () => {
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentProductIndex, setCurrentProductIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transitionLoading, setTransitionLoading] = useState(false);

  const dispatch = useDispatch();
  const wishlistSlugs = useSelector(state => state.wishlist?.wishlistSlugs || []);

  useEffect(() => {
    const loadWishlistProducts = async () => {
      if (!wishlistSlugs.length) {
        setWishlistProducts([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const products = await fetchWishlistProducts(wishlistSlugs);
        
        // Additional validation
        const validProducts = products.filter(product => 
          product && 
          product.ProductId && 
          product.Slug && 
          product.Colors?.length > 0
        );
        
        if (validProducts.length !== wishlistSlugs.length) {
          console.warn('Some products could not be loaded');
        }
        
        setWishlistProducts(validProducts);
      } catch (error) {
        console.error("Error loading wishlist products:", error);
        setError(error);
        toast.error("Error loading wishlist products");
      } finally {
        setLoading(false);
      }
    };

    loadWishlistProducts();
  }, [wishlistSlugs]);

  const clearWishlistCompletely = useCallback(() => {
    dispatch(clearWishlist());
    setCurrentProductIndex(null);
    setIsModalOpen(false);
    setWishlistProducts([]);
    toast.success("Wishlist cleared");
  }, [dispatch]);

  const handleAddAllToCart = useCallback(() => {
    if (wishlistProducts.length > 0) {
      setCurrentProductIndex(0);
      setIsModalOpen(true);
    } else {
      toast.error('No products in wishlist');
    }
  }, [wishlistProducts.length]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setTransitionLoading(true);

    setTimeout(() => {
      if (currentProductIndex !== null) {
        const nextIndex = currentProductIndex + 1;
        if (nextIndex < wishlistProducts.length) {
          setCurrentProductIndex(nextIndex);
          setIsModalOpen(true);
        } else {
          clearWishlistCompletely();
          toast.success("All items have been processed");
        }
      }
      setTransitionLoading(false);
    }, 500);
  }, [currentProductIndex, wishlistProducts.length, clearWishlistCompletely]);

  const getCurrentProduct = useCallback(() => {
    if (currentProductIndex === null || !wishlistProducts[currentProductIndex]) {
      return null;
    }
    const product = wishlistProducts[currentProductIndex];
    return product?.Slug && product?.Colors?.length > 0 ? product : null;
  }, [currentProductIndex, wishlistProducts]);

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="wishlist-error">
        Error loading wishlist products: {error.message}
      </div>
    );
  }

  const currentProduct = getCurrentProduct();

  return (
    <div className="wishlist-page">
      <div className="wishlist-header-section">
        <h1 className="wishlist-heading">Wishlist</h1>
        <hr />
        {wishlistProducts.length > 0 && (
          <div className="wishlist-button-container">
            <button 
              className="wishlist-add-to-cart-button" 
              onClick={handleAddAllToCart}
              disabled={transitionLoading}
            >
              <i className="fa-solid fa-cart-shopping"></i>
              Add All to Cart
            </button>
          </div>
        )}
      </div>
      
      {wishlistProducts.length === 0 ? (
        <p className="wishlist-no-products">No products in your wishlist</p>
      ) : (
        <div className="wishlist-content">
          <div className="wishlist-grid">
            {wishlistProducts.map(product => (
              <ProductCard 
                key={`${product.ProductId}-${product.Slug}`}
                product={product}
                onAddToCart={handleAddAllToCart}
              />
            ))}
          </div>
        </div>
      )}

      {transitionLoading && wishlistProducts.length > 0 && (
        <TransitionLoader message="Processing..." />
      )}

      {isModalOpen && currentProduct && (
        <ProductViewModal
          key={`modal-${currentProduct.ProductId}-${currentProductIndex}`}
          slug={currentProduct.Slug}
          preSelectedSize={currentProduct.Colors?.[0]?.Size}
          preSelectedColor={currentProduct.Colors?.[0]?.Color}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default WishlistPage;